<template>
  <div class="space-y-10">
    <div class="space-y-3">
      <p class="app-eyebrow">{{ t("onboarding.personalEyebrow") }}</p>
      <h1 class="text-3xl font-bold tracking-tight">
        {{ t("onboarding.personalTitle") }}
      </h1>
      <p class="max-w-3xl text-sm leading-7 text-brand-muted-strong">
        {{ t("onboarding.personalDescription") }}
      </p>

    </div>

    <Card class="rounded-none border-brand-line bg-brand-surface shadow-none">
      <CardHeader class="p-8 pb-0">
        <div class="flex items-center gap-2">
          <User class="size-5 text-brand-ink" />
          <CardTitle class="text-xl font-bold">{{
            t("onboarding.profileTitle")
          }}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="p-8 pt-2">
        <div
            class="mb-5 flex w-full items-start gap-3 border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950"
        >
          <AlertCircle class="mt-0.5 size-4 shrink-0" />
          <p>{{ t("onboarding.personalDocumentNotice") }}</p>
        </div>
        <div class="grid gap-x-10 gap-y-7 md:grid-cols-2">
          <Field :label="t('onboarding.field.salutation')">
            <select v-model="draft.salutation" class="app-field">
              <option value="Herr">{{ t("onboarding.salutation.mr") }}</option>
              <option value="Frau">{{ t("onboarding.salutation.ms") }}</option>
              <option value="Divers">{{ t("onboarding.salutation.diverse") }}</option>
            </select>
          </Field>
          <Field :label="t('onboarding.field.title')">
            <input v-model.trim="draft.titleBefore" class="app-field" type="text" />
          </Field>
          <Field :label="t('onboarding.field.firstName')" :error="errors.firstName">
            <input
              v-model.trim="draft.firstName"
              class="app-field"
              type="text"
              autocomplete="given-name"
            />
          </Field>
          <Field :label="t('onboarding.field.lastName')" :error="errors.lastName">
            <input
              v-model.trim="draft.lastName"
              class="app-field"
              type="text"
              autocomplete="family-name"
            />
          </Field>
          <Field :label="t('onboarding.field.birthDate')" :error="errors.birthDate">
            <input v-model="draft.birthDate" class="app-field" type="date" />
          </Field>
          <Field :label="t('onboarding.field.nationality')" :error="errors.nationality">
            <select v-model="draft.nationality" class="app-field">
              <option value="">{{ t("onboarding.choose") }}</option>
              <option
                v-for="country in nationalityCountries"
                :key="country"
                :value="country"
              >
                {{ t(`onboarding.country.${country.toLowerCase()}`) }}
              </option>
            </select>
          </Field>
          <Field :label="t('onboarding.field.birthPlace')" :error="errors.birthPlace">
            <input v-model.trim="draft.birthPlace" class="app-field" type="text" />
          </Field>
        </div>
      </CardContent>
    </Card>

    <Card class="rounded-none border-brand-line bg-brand-surface shadow-none">
      <CardHeader class="p-8 pb-4">
        <div class="flex items-center gap-2">
          <MapPin class="size-5 text-brand-ink" />
          <CardTitle class="text-xl font-bold">{{
            t("onboarding.addressTitle")
          }}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="p-8 pt-2">
        <div class="grid gap-x-10 gap-y-7 md:grid-cols-2">
          <Field :label="t('onboarding.field.street')" :error="errors.streetName">
            <input
              v-model.trim="draft.streetName"
              class="app-field"
              type="text"
              autocomplete="street-address"
            />
          </Field>
          <Field :label="t('onboarding.field.houseNumber')" :error="errors.houseNumber">
            <input v-model.trim="draft.houseNumber" class="app-field" type="text" />
          </Field>
          <Field :label="t('onboarding.field.postCode')" :error="errors.postCode">
            <input
              v-model.trim="draft.postCode"
              class="app-field"
              type="text"
              autocomplete="postal-code"
            />
          </Field>
          <Field :label="t('onboarding.field.city')" :error="errors.city">
            <input
              v-model.trim="draft.city"
              class="app-field"
              type="text"
              autocomplete="address-level2"
            />
          </Field>
          <Field :label="t('onboarding.field.country')" :error="errors.country">
            <select v-model="draft.country" class="app-field">
              <option value="">{{ t("onboarding.choose") }}</option>
              <option value="AT">{{ t("onboarding.country.at") }}</option>
              <option value="DE">{{ t("onboarding.country.de") }}</option>
              <option value="CH">{{ t("onboarding.country.ch") }}</option>
            </select>
          </Field>
          <Field :label="t('onboarding.field.mobile')" :error="errors.mobileNumber">
            <input
              v-model.trim="draft.mobileNumber"
              class="app-field"
              type="tel"
              autocomplete="tel"
            />
          </Field>
        </div>
      </CardContent>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { defineComponent, h } from "vue";
import { AlertCircle, MapPin, User } from "lucide-vue-next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useI18n } from "@/lib/i18n";
import type { OnboardingFormModel } from "@/features/onboarding";

defineProps<{
  draft: OnboardingFormModel;
  errors: Record<string, string>;
}>();

const { t } = useI18n();

const nationalityCountries = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
  "DE", "GR", "HU", "IS", "IE", "IT", "LV", "LI", "LT", "LU",
  "MT", "NL", "NO", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "CH",
];

const Field = defineComponent({
  props: {
    label: { type: String, required: true },
    error: { type: String, required: false, default: "" },
  },
  setup(props, { slots }) {
    return () =>
      h(
        "label",
        { class: "block space-y-2 text-sm font-medium text-brand-muted-strong" },
        [
          h("span", props.label),
          slots.default?.(),
          props.error
            ? h("span", { class: "mt-1 block text-xs font-bold text-destructive" }, props.error)
            : null,
        ],
      );
  },
});
</script>
